"use client";

import { c as _c } from "react/compiler-runtime";
import { faExternalLinkSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { Accordion, Col, Container, Row } from "react-bootstrap";
import { NULL_ADDRESS } from "@/constants";
import { NextGenCollection, NextGenLog } from "@/entities/INextgen";
import { areEqualAddresses, formatAddress, getDateDisplay, getTransactionLink } from "@/helpers/Helpers";
import { commonApiFetch } from "@/services/api/common-api";
import { printGas, printRoyalties } from "@/components/latest-activity/LatestActivityRow";
import Pagination from "@/components/pagination/Pagination";
import { NEXTGEN_CHAIN_ID } from "@/components/nextGen/nextgen_contracts";
import styles from "../NextGen.module.scss";
import { getNextGenIconUrl, getNextGenImageUrl } from "../nextgenToken/NextGenTokenImage";
interface Props {
  collection: NextGenCollection;
}
const PAGE_SIZE = 20;
export default function NextGenCollectionProvenance(props) {
  const $ = _c(22);
  const scrollTarget = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [logs, setLogs] = useState(t0);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  let t1;
  if ($[1] !== props.collection.id) {
    t1 = function fetchResults(mypage) {
      setLogsLoaded(false);
      commonApiFetch({
        endpoint: `nextgen/collections/${props.collection.id}/logs?page_size=${PAGE_SIZE}&page=${mypage}`
      }).then(response => {
        setTotalResults(response.count);
        setLogs(response.data);
        setLogsLoaded(true);
      });
    };
    $[1] = props.collection.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const fetchResults = t1;
  let t2;
  if ($[3] !== fetchResults || $[4] !== page) {
    t2 = () => {
      fetchResults(page);
    };
    $[3] = fetchResults;
    $[4] = page;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== page) {
    t3 = [page];
    $[6] = page;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== logs || $[9] !== props.collection) {
    let t5;
    if ($[11] !== props.collection) {
      t5 = (log, index) => <NextGenCollectionProvenanceRow collection={props.collection} log={log} key={`${log.id}`} odd={index % 2 !== 0} />;
      $[11] = props.collection;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = logs.map(t5);
    $[8] = logs;
    $[9] = props.collection;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = <Row className="pt-2"><Col>{t4}</Col></Row>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== logsLoaded || $[16] !== page || $[17] !== totalResults) {
    t6 = totalResults > PAGE_SIZE && logsLoaded && <Row className="text-center pt-4 pb-4"><Pagination page={page} pageSize={PAGE_SIZE} totalResults={totalResults} setPage={function (newPage) {
        setPage(newPage);
        if (scrollTarget.current) {
          scrollTarget.current.scrollIntoView({
            behavior: "smooth"
          });
        }
      }} /></Row>;
    $[15] = logsLoaded;
    $[16] = page;
    $[17] = totalResults;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <Container className="no-padding" ref={scrollTarget}>{t5}{t6}</Container>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
interface NextGenCollectionProvenanceRowProps {
  collection: NextGenCollection;
  log: NextGenLog;
  disable_link?: boolean;
}
export function NextGenCollectionProvenanceRow(props: Readonly<{
  collection: NextGenCollection;
  log: NextGenLog;
  disable_link?: boolean;
  odd?: boolean;
}>) {
  const log = props.log;
  const [isTransaction, setIsTransaction] = useState<boolean>(false);
  function printAddress(address: string, display?: string) {
    return <Link href={`/${address}`}>{display ?? formatAddress(address)}</Link>;
  }
  function printParsedLog() {
    const escapedCollectionName = props.collection.name.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
    const pattern = new RegExp("(" + escapedCollectionName + " #(\\d+))");
    const match = pattern.exec(log.log);
    try {
      if (match?.[1] && match?.[2] && typeof match.index === "number") {
        if (!isTransaction) {
          setIsTransaction(true);
        }
        const startIndex = match.index;
        const endIndex = startIndex + match[1].length;
        const beforeMatch = log.log.substring(0, startIndex);
        let afterMatch = log.log.substring(endIndex);
        if (afterMatch.startsWith(" ")) {
          afterMatch = afterMatch.substring(1);
        }
        const normalisedTokenId = parseInt(match[2], 10);
        const tokenId = props.collection.id * 10000000000 + normalisedTokenId;
        const content = <>
            {match[1]}
            <Image unoptimized width={0} height={0} style={{
            height: "40px",
            width: "auto",
            marginLeft: "8px",
            marginRight: "8px"
          }} src={getNextGenIconUrl(tokenId)} alt={`#${tokenId.toString()}`} className={styles.nftImage} onError={({
            currentTarget
          }) => {
            if (currentTarget.src === getNextGenIconUrl(tokenId)) {
              currentTarget.src = getNextGenImageUrl(tokenId);
            }
          }} />
          </>;
        let fromTo: any;
        if (isTransaction) {
          fromTo = <span className="d-flex gap-1">
              <span>
                {areEqualAddresses(log.from_address, NULL_ADDRESS) ? "Minted" : <>
                    from&nbsp;
                    {printAddress(log.from_address, log.from_display)}
                  </>}
              </span>
              <span>
                to&nbsp;
                {printAddress(log.to_address, log.to_display)}
              </span>
            </span>;
        }
        const beforeMatchSpan = <span>{beforeMatch}</span>;
        const afterMatchSpan = afterMatch ? <span>{afterMatch}&nbsp;</span> : <></>;
        if (props.disable_link) {
          return <>
              {beforeMatchSpan}
              {content}
              {afterMatchSpan}
              {fromTo}
            </>;
        } else {
          return <>
              {beforeMatchSpan}
              &nbsp;
              <Link href={`/nextgen/token/${tokenId}`} onClick={e_0 => e_0.stopPropagation()}>
                {content}
              </Link>
              {afterMatchSpan}
              {fromTo}
            </>;
        }
      }
    } catch (e) {
      console.error("Error processing log:", e);
    }
    return <>{log.heading}</>;
  }
  function printBody() {
    if (!isTransaction) {
      const logSpan = <span>{log.log}</span>;
      if (log.log.startsWith("Script at index")) {
        return <span className="d-flex flex-column">
            <span className="font-smaller font-color-h pb-2">
              * The script of each collection is split into manageable chunks
              due to Ethereum&apos;s transaction size limits. Each chunk of the
              script is updated individually in a separate transaction.
            </span>
            {logSpan}
          </span>;
      }
      return logSpan;
    }
  }
  return <Accordion className={props.odd ? styles.collectionProvenanceAccordionOdd : styles.collectionProvenanceAccordion}>
      <Accordion.Item defaultChecked={true} eventKey={"0"}>
        <Accordion.Button className={`d-flex justify-content-between ${isTransaction ? styles.collectionProvenanceAccordionButtonHideCaret : ""}`}>
          <Container className={styles.collectionProvenanceAccordionButton}>
            <Row>
              <Col>
                <span className="d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-4">
                    <span className="no-wrap">
                      {getDateDisplay(new Date(log.block_timestamp * 1000))}
                    </span>
                    <span className="d-flex align-items-center">
                      {printParsedLog()}
                    </span>
                  </span>
                  <span className="d-flex align-items-center gap-2">
                    {isTransaction && printGas(log.gas, log.gas_price, log.gas_price_gwei)}
                    {isTransaction && printRoyalties(log.value, log.royalties, log.from_address)}
                    <Link href={getTransactionLink(NEXTGEN_CHAIN_ID, log.transaction)} onClick={e_1 => e_1.stopPropagation()} target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon style={{
                      height: "25px",
                      cursor: "pointer"
                    }} icon={faExternalLinkSquare}></FontAwesomeIcon>
                    </Link>
                  </span>
                </span>
              </Col>
            </Row>
          </Container>
        </Accordion.Button>
        {!isTransaction && <Accordion.Body className={props.odd ? styles.collectionProvenanceAccordionBodyOdd : styles.collectionProvenanceAccordionBody}>
            <Container className="no-padding">
              <Row className="pt-2 pb-2">
                <Col>{printBody()}</Col>
              </Row>
            </Container>
          </Accordion.Body>}
      </Accordion.Item>
    </Accordion>;
}